import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import {
  Storefront, EnvelopeSimple, Phone, MapPin, Trash, PencilSimple,
  ArrowClockwise, Tray, Clock, CheckSquare, Funnel
} from "@phosphor-icons/react";

const STATUS_TONE = { 
  new: "yellow", 
  contacted: "orange", 
  converted: "green", 
  closed: "zinc" 
};
const STATUSES = ["new", "contacted", "converted", "closed"];

export default function Enquiries() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, distributors: 0 });
  const [statusFilter, setStatusFilter] = useState("new");
  const [distFilter, setDistFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (distFilter === "yes") params.distributor = true;
    if (distFilter === "no") params.distributor = false;
    const [a, b] = await Promise.all([
      api.get("/contact", { params }),
      api.get("/contact/summary"),
    ]);
    setRows(a.data);
    setSummary(b.data);
  }, [statusFilter, distFilter]);

  useEffect(() => { load(); }, [load]);

  const del = async (id, name) => {
    const ok = await confirm({
      title: "Delete enquiry?",
      message: `"${name}" will be permanently removed from your inbox.`,
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/contact/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8 animate-fade-in">
      <PageHeader
        label="INBOX"
        title="Website Enquiries"
        subtitle={`${summary.new} new · ${summary.distributors} distributor enquiries · ${summary.total} total`}
        action={
          <Btn 
            variant="secondary" 
            onClick={load} 
            data-testid="enq-refresh"
            className="flex items-center gap-2 shadow-sm border-zinc-200 hover:border-zinc-300 transition-all active:scale-98"
          >
            <ArrowClockwise size={15} className="text-zinc-600" /> Refresh
          </Btn>
        }
      />

      {/* Modern Analytics Widget Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
        <StatCard label="New Submissions" value={summary.new} tone="yellow" icon={<Clock size={22} />} />
        <StatCard label="Distributors" value={summary.distributors} tone="orange" icon={<Storefront size={22} />} />
        <StatCard label="Total Received" value={summary.total} tone="zinc" icon={<Tray size={22} />} />
      </div>

      <Card className="!p-0 border border-zinc-200/80 shadow-sm overflow-hidden bg-white rounded-xl">
        {/* Interactive Controls Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-zinc-50/50 border-b border-zinc-200/80">
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex items-center gap-2">
              <Funnel size={14} className="text-zinc-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Filters:</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-600 font-medium">Status</span>
              <Select 
                value={statusFilter} 
                onChange={e => setStatusFilter(e.target.value)} 
                className="!w-auto !py-1 !text-xs rounded-lg shadow-2xs border-zinc-200" 
                data-testid="enq-status-filter"
              >
                <option value="">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-600 font-medium">Type</span>
              <Select 
                value={distFilter} 
                onChange={e => setDistFilter(e.target.value)} 
                className="!w-auto !py-1 !text-xs rounded-lg shadow-2xs border-zinc-200" 
                data-testid="enq-dist-filter"
              >
                <option value="">All Types</option>
                <option value="yes">Distributor</option>
                <option value="no">General</option>
              </Select>
            </div>
          </div>
        </div>

        {/* Dynamic Rows Container */}
        {rows.length === 0 ? (
          <div className="p-12 text-center">
            <EmptyState title="No enquiries found" hint="Submissions from your website contact form will appear here." />
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {isAdmin && rows.length > 0 && (
              <div className="px-5 py-2.5 bg-zinc-50/80 flex items-center gap-3 border-b border-zinc-100 text-xs">
                <input
                  type="checkbox"
                  checked={ms.selected.length > 0 && ms.selected.length === rows.length}
                  onChange={() => ms.toggleAll(rows.map(x => x.id))}
                  className="w-4 height-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 accent-orange-600 cursor-pointer"
                />
                <span className="text-zinc-500 font-medium flex items-center gap-1">
                  <CheckSquare size={13} /> Select all rows ({rows.length})
                </span>
              </div>
            )}
            {rows.map(r => (
              <div key={r.id} className="flex items-start gap-4 px-5 py-1 hover:bg-zinc-50/40 transition-colors group">
                {isAdmin && (
                  <div className="pt-6">
                    <input
                      type="checkbox"
                      checked={ms.isSelected(r.id)}
                      onChange={() => ms.toggle(r.id)}
                      className="w-4 height-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 accent-orange-600 cursor-pointer"
                      data-testid={`enquiry-check-${r.id}`}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <EnquiryRow row={r} onEdit={() => setEditing(r)} onDelete={() => del(r.id, r.name)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && (
        <EditModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="enquiries"
        label="enquiries"
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function StatCard({ label, value, tone, icon }) {
  const colors = { 
    yellow: "bg-gradient-to-br from-amber-50/60 to-yellow-50/30 border-amber-100/80 text-amber-900 icon:text-amber-600",
    orange: "bg-gradient-to-br from-orange-50/60 to-amber-50/30 border-orange-100/80 text-orange-900 icon:text-orange-600",
    zinc: "bg-gradient-to-br from-zinc-50 to-zinc-100/50 border-zinc-200/80 text-zinc-950 icon:text-zinc-500" 
  }[tone] || "bg-white border-zinc-200 text-zinc-900";

  return (
    <div className={`border p-5 rounded-xl shadow-xs flex items-center justify-between transition-all hover:shadow-sm ${colors}`}>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500/90">{label}</div>
        <div className="text-3xl font-extrabold tracking-tight mt-1.5">{value}</div>
      </div>
      <div className="p-2.5 rounded-lg bg-white/80 border border-current/5 shadow-2xs">
        {icon}
      </div>
    </div>
  );
}

function EnquiryRow({ row, onEdit, onDelete }) {
  return (
    <div className="py-5" data-testid={`enq-row-${row.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          {/* Metadata Badges */}
          <div className="flex items-center flex-wrap gap-2">
            {row.is_distributor && (
              <Badge tone="orange" className="inline-flex items-center gap-1 shadow-2xs !py-0.5 !px-2 rounded font-medium">
                <Storefront size={12} weight="bold" /> Distributor
              </Badge>
            )}
            <Badge tone={STATUS_TONE[row.status] || "zinc"} className="capitalize shadow-2xs !py-0.5 !px-2 rounded font-medium">
              {row.status}
            </Badge>
            <span className="text-xs text-zinc-400 font-medium">
              {new Date(row.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
            </span>
          </div>

          {/* Heading Identification */}
          <div>
            <h3 className="font-extrabold text-lg text-zinc-900 tracking-tight flex items-baseline flex-wrap">
              {row.name}
              {row.firm_name && (
                <span className="text-zinc-500 font-normal text-sm ml-2 border-l border-zinc-200 pl-2">
                  {row.firm_name}
                </span>
              )}
            </h3>
          </div>

          {/* Communication Links */}
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-zinc-600 font-medium">
            {row.email && (
              <a href={`mailto:${row.email}`} className="flex items-center gap-1.5 hover:text-orange-600 transition-colors py-0.5">
                <EnvelopeSimple size={14} className="text-zinc-400" /> {row.email}
              </a>
            )}
            {row.phone && (
              <a href={`tel:${row.phone}`} className="flex items-center gap-1.5 hover:text-orange-600 transition-colors py-0.5">
                <Phone size={14} className="text-zinc-400" /> {row.phone}
              </a>
            )}
            {row.location && (
              <span className="flex items-center gap-1.5 text-zinc-500 py-0.5">
                <MapPin size={14} className="text-zinc-400" /> {row.location}
              </span>
            )}
          </div>

          {/* Conditional Distributor Data */}
          {row.is_distributor && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              {row.products_sold && (
                <div className="bg-zinc-50 border border-zinc-100 p-2.5 rounded-lg shadow-2xs">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Current Distribution</div>
                  <div className="mt-0.5 text-xs text-zinc-700 font-medium">{row.products_sold}</div>
                </div>
              )}
              {row.coverage && (
                <div className="bg-zinc-50 border border-zinc-100 p-2.5 rounded-lg shadow-2xs">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Territory Coverage</div>
                  <div className="mt-0.5 text-xs text-zinc-700 font-medium">{row.coverage}</div>
                </div>
              )}
            </div>
          )}

          {/* User Message Block */}
          {row.message && (
            <div className="mt-3 bg-zinc-50/40 border border-zinc-100 text-sm text-zinc-700 p-3 rounded-lg leading-relaxed whitespace-pre-wrap relative">
              <span className="absolute left-0 top-0 text-zinc-200 text-3xl font-serif select-none pl-1 leading-none">&ldquo;</span>
              <div className="pl-4 pr-2">{row.message}</div>
            </div>
          )}

          {/* Administrative Internal Notes */}
          {row.admin_notes && (
            <div className="mt-2 bg-amber-50/60 border border-amber-100 p-2.5 rounded-lg text-xs text-amber-900 flex items-start gap-2">
              <span className="font-bold text-amber-800 shrink-0 uppercase tracking-wide text-[10px] bg-amber-100 px-1.5 py-0.5 rounded">Team Note</span> 
              <span className="font-medium">{row.admin_notes}</span>
            </div>
          )}
        </div>

        {/* Row Row Actions Column */}
        <div className="flex sm:flex-col gap-2 items-center sm:items-end justify-end pt-1 shrink-0 opacity-90 sm:opacity-40 group-hover:opacity-100 transition-opacity">
          <button
            data-testid={`enq-edit-${row.id}`}
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 hover:bg-orange-600 active:scale-98 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-xs"
          >
            <PencilSimple size={12} weight="bold" /> Update
          </button>
          <button
            data-testid={`enq-del-${row.id}`}
            onClick={onDelete}
            className="p-1.5 text-zinc-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-all"
            title="Delete Enquiry"
          >
            <Trash size={15} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [status, setStatus] = useState(row.status || "new");
  const [notes, setNotes] = useState(row.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/contact/${row.id}`, { status, admin_notes: notes });
      toast.success("Updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Update Record · ${row.name}`}>
      <form onSubmit={submit} className="space-y-5 p-1" data-testid="enq-edit-form">
        <div className="bg-zinc-50 border border-zinc-200/80 p-3 rounded-lg text-xs font-mono space-y-1 shadow-2xs">
          {row.is_distributor && row.firm_name && <div className="font-bold text-zinc-800">{row.firm_name}</div>}
          <div className="text-zinc-500 flex items-center gap-2 flex-wrap">
            <span>{row.email}</span>
            {row.phone && <span className="text-zinc-300">|</span>}
            <span>{row.phone}</span>
          </div>
        </div>
        
        <Field label="System Status">
          <Select 
            value={status} 
            onChange={e => setStatus(e.target.value)} 
            data-testid="enq-edit-status"
            className="w-full mt-1 rounded-lg border-zinc-300 shadow-xs text-sm focus:border-orange-500 focus:ring-orange-500"
          >
            {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </Select>
        </Field>
        
        <Field label="Internal Team Notes">
          <textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="w-full mt-1 border border-zinc-300 rounded-lg px-3 py-2.5 shadow-xs focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none text-sm leading-relaxed"
            placeholder="e.g. Discussed bulk requirements, sending customized catalogue copies next Tuesday..."
            data-testid="enq-edit-notes"
          />
        </Field>
        
        <div className="flex justify-end gap-3 pt-2 border-t border-zinc-100">
          <Btn 
            type="button" 
            variant="secondary" 
            onClick={onClose}
            className="rounded-lg px-4 border-zinc-200 text-zinc-700 hover:bg-zinc-50"
          >
            Cancel
          </Btn>
          <Btn 
            type="submit" 
            disabled={busy} 
            data-testid="enq-edit-save"
            className="rounded-lg px-5 bg-zinc-950 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors shadow-sm"
          >
            {busy ? "Saving adjustments…" : "Save Changes"}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}